import React, { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AppShell, PageHeader } from '@/components/AppShell';
import { api } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { Camera, Plus, ArrowRight, ShieldCheck, Loader2, Images, Hash } from 'lucide-react';
import { VehicleTrustBadge, StarRating } from '@/components/VehicleTrust';

async function compress(file, maxW = 1024, quality = 0.6) {
  const img = document.createElement('img');
  const reader = new FileReader();
  const dataUrl = await new Promise((res, rej) => { reader.onload = () => res(reader.result); reader.onerror = rej; reader.readAsDataURL(file); });
  await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = dataUrl; });
  const scale = Math.min(1, maxW / img.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', quality);
}

export default function VehiclePhoto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const inputRef = useRef(null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [plate, setPlate] = useState('');
  const [plateBusy, setPlateBusy] = useState(false);
  const [trust, setTrust] = useState(null);
  const [myRating, setMyRating] = useState(0);
  const [rateBusy, setRateBusy] = useState(false);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= 3) { toast.info(t('vp_max')); return; }
    setBusy(true);
    try {
      const dataUrl = await compress(file);
      const saved = await api.addPhoto(id, { data_url: dataUrl, photo_type: 'vehicle' });
      setPhotos((p) => [...p, saved]);
      api.track('vehicle_photo_added');
    } catch { toast.error(t('vp_fail')); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  const goActive = () => navigate(`/app/ride/${id}`, { replace: true });

  const savePlate = async () => {
    if (!plate.trim()) return;
    setPlateBusy(true);
    try {
      const res = await api.setVehicle(id, { plate_number: plate.trim() });
      setTrust(res);
      api.track('vehicle_number_added');
      toast.success(t('vp_saved_plate'));
    } catch { toast.error(t('vp_fail')); }
    finally { setPlateBusy(false); }
  };

  const rate = async (n) => {
    if (!trust) return;
    setMyRating(n);
    setRateBusy(true);
    try {
      const res = await api.rateVehicle(trust.plate_normalized || plate.trim(), { ride_id: id, rating: n });
      setTrust((prev) => ({ ...prev, rating_avg: res.rating_avg, rating_count: res.rating_count }));
      api.track('vehicle_rated', { rating: n });
      toast.success(t('vt_rated'));
    } catch { toast.error(t('vp_fail')); }
    finally { setRateBusy(false); }
  };

  return (
    <AppShell nav={false}>
      <PageHeader title={t('vp_title')} subtitle={t('vp_sub')} />
      <input id="vehicle-photo-file" ref={inputRef} type="file" accept="image/*" capture="environment" onChange={onPick} data-testid="vehicle-photo-input" style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }} />
      <input id="vehicle-gallery-file" type="file" accept="image/*" onChange={onPick} data-testid="vehicle-gallery-input" style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }} />

      <div className="grid grid-cols-3 gap-2 mb-2">
        {photos.map((p) => (
          <div key={p.id} className="relative rounded-xl overflow-hidden border" style={{ borderColor: 'var(--border)', aspectRatio: '1' }}>
            <img src={p.data_url} alt="Vehicle" className="w-full h-full object-cover" />
          </div>
        ))}
        {photos.length < 3 && (
          <label htmlFor="vehicle-photo-file" role="button" tabIndex={0} aria-label={photos.length === 0 ? t('vp_take') : t('vp_add')} className="rounded-xl border flex flex-col items-center justify-center gap-1 cursor-pointer" style={{ borderColor: 'var(--primary)', background: 'var(--surface-alt)', aspectRatio: '1', pointerEvents: busy ? 'none' : 'auto', opacity: busy ? 0.7 : 1 }} data-testid="vehicle-photo-add-button">
            {busy ? <Loader2 size={22} className="animate-spin" style={{ color: 'var(--primary)' }} /> : photos.length === 0 ? <Camera size={22} style={{ color: 'var(--primary)' }} /> : <Plus size={22} style={{ color: 'var(--primary)' }} />}
            <span className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>{photos.length === 0 ? t('vp_take') : t('vp_add')}</span>
          </label>
        )}
      </div>

      {photos.length < 3 && (
        <label htmlFor="vehicle-gallery-file" role="button" className="w-full mb-4 inline-flex items-center justify-center gap-2 rounded-xl font-semibold min-h-[44px] border cursor-pointer text-sm" style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--text)', pointerEvents: busy ? 'none' : 'auto', opacity: busy ? 0.7 : 1 }} data-testid="vehicle-gallery-button">
          <Images size={18} style={{ color: 'var(--primary)' }} /> {t('vp_gallery')}
        </label>
      )}

      {/* Vehicle number -> trust data */}
      <div className="rounded-2xl p-3 border mb-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2">
          <Hash size={16} style={{ color: 'var(--primary)' }} />
          <input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder={t('vp_plate_ph')} className="flex-1 bg-transparent outline-none text-sm" style={{ color: 'var(--text)' }} data-testid="vehicle-plate-input" />
          <button onClick={savePlate} disabled={plateBusy || !plate.trim()} className="rounded-lg px-3 py-2 text-xs font-semibold disabled:opacity-50" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }} data-testid="vehicle-plate-save">
            {plateBusy ? <Loader2 size={14} className="animate-spin" /> : t('vp_save_plate')}
          </button>
        </div>
        {trust ? (
          <div className="mt-3 space-y-3" data-testid="vehicle-trust-panel">
            <VehicleTrustBadge trust={trust} />
            <div className="pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
              <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text)' }}>{t('vt_rate_prompt')}</p>
              <div className="flex items-center gap-3">
                <StarRating value={myRating} onChange={rate} testId="vp-rate-stars" />
                {rateBusy && <Loader2 size={16} className="animate-spin" style={{ color: 'var(--primary)' }} />}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>{t('vp_plate_hint')}</p>
        )}
      </div>

      <div className="rounded-2xl p-3 border mb-2 flex items-start gap-2" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <ShieldCheck size={18} style={{ color: 'var(--primary)' }} />
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          <p className="font-semibold" style={{ color: 'var(--text)' }}>{t('vp_safe1')}</p>
          <p>{t('vp_safe2')}</p>
          <p className="mt-1">{t('vp_safe3')}</p>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button data-testid="vehicle-photo-skip-button" onClick={goActive} className="flex-1 rounded-full font-semibold min-h-[52px] border" style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--surface)' }}>{t('skip')}</button>
        <button data-testid="vehicle-photo-continue-button" onClick={goActive} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full font-bold min-h-[52px]" style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>{t('vp_continue')} <ArrowRight size={20} /></button>
      </div>
    </AppShell>
  );
}
